import { graphql, useFragment, useMutation } from "react-relay";
import type { postBottomActionsFragment$key } from "./__generated__/postBottomActionsFragment.graphql";
import HandThumbDownIcon from "@heroicons/react/24/outline/HandThumbDownIcon";
import HandThumbUpIcon from "@heroicons/react/24/outline/HandThumbUpIcon";
import ChatBubbleOvalLeftEllipsisIcon from "@heroicons/react/24/outline/ChatBubbleOvalLeftEllipsisIcon";
import { Link, useRouteContext } from "@tanstack/react-router";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import { toast } from "sonner";
import { Icon } from "@/components/icon";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import { cn } from "@/utils/cn";
import type { postBottomActionsCreateVoteMutation } from "./__generated__/postBottomActionsCreateVoteMutation.graphql";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { useState } from "react";
import { JoinModal } from "@/dialogs/join-dialog";
import {
	Tooltip,
	TooltipTrigger,
	TooltipContent,
} from "@/components/ui/tooltip";

export function PostBottomActions({
	post,
}: {
	post: postBottomActionsFragment$key;
}) {
	const { session } = useRouteContext({ from: "__root__" });
	const { track } = useAnalytics();
	const [joinModalOpen, setJoinModalOpen] = useState(false);

	const [_, copy] = useCopyToClipboard();
	const data = useFragment<postBottomActionsFragment$key>(
		graphql`
    fragment postBottomActionsFragment on Post {
      id
      slug
      url
      space {
        slug
      }
      userVote {
        vote
      }
      commentCount
      author {
	      id
      }
      votes
    }
  `,
		post,
	);
	const [vote, _isVoting] =
		useMutation<postBottomActionsCreateVoteMutation>(graphql`
	  mutation postBottomActionsCreateVoteMutation ($postId: ID!, $vote: PostVoteType!) {
			registerVote(postId: $postId, vote: $vote) {
				id
				...on Post {
				  votes
	   			userVote {
	   			  vote
	   			}
				}
			}
		}
	`);

	return (
		<Dialog open={joinModalOpen} onOpenChange={setJoinModalOpen}>
			<div className="flex flex-row justify-between">
				<div className="flex flex-row items-center">
					<div className="flex flex-row items-center justify-center w-fit gap-0">
						<Button
							type="button"
							size="icon"
							variant="ghost"
							onClick={() => {
								if (!session?.user) {
									setJoinModalOpen(true);
									return;
								}

								vote({ variables: { postId: data.id, vote: "UP" } });
								track(AnalyticsEvents.POST_VOTED, {
									post_id: data.id,
									vote: "up",
								});
							}}
							className={cn(
								data.userVote?.vote === "UP" &&
									"text-green-600 dark:text-green-500",
							)}
						>
							<HandThumbUpIcon className="size-5" />
						</Button>

						<div className="text-center text-foreground w-fit px-2 font-medium">
							{data.votes}
						</div>

						<Button
							type="button"
							size="icon"
							variant="ghost"
							className={cn(
								data.userVote?.vote === "DOWN" && "text-destructive",
							)}
							onClick={() => {
								if (!session?.user) {
									setJoinModalOpen(true);
									return;
								}

								vote({ variables: { postId: data.id, vote: "DOWN" } });
								track(AnalyticsEvents.POST_VOTED, {
									post_id: data.id,
									vote: "down",
								});
							}}
						>
							<HandThumbDownIcon className="size-5" />
						</Button>
					</div>

					<Link
						to="/comunidade/s/$space/posts/$post"
						params={{ space: data.space?.slug!, post: data.slug }}
						className="ml-6"
					>
						<Button variant="ghost">
							<ChatBubbleOvalLeftEllipsisIcon className="size-5" />

							{data.commentCount}
						</Button>
					</Link>

					{data?.url && (
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									type="button"
									variant="ghost"
									className="ml-6"
									onClick={() => {
										track(AnalyticsEvents.POST_SHARED, { post_id: data.id });
										toast.success("Link copiado para a área de transferência!");
										copy(data.url!);
									}}
								>
									<Icon name="Share2" className="size-4" />
								</Button>
							</TooltipTrigger>
							<TooltipContent>Compartilhar</TooltipContent>
						</Tooltip>
					)}
				</div>
			</div>

			<JoinModal />
		</Dialog>
	);
}
